/**
 * settingsStyles — the prototype-aligned style vocabulary for the HouseholdProduct
 * settings surface (T12.1.7.1). One place so the detail page, the SystemProduct
 * picker, and the brand/retailer/store/cadence settings all render the same
 * `.setgroup` / `.tag` / `.pinrow` / `.stepper` / `.miniadd` affordances.
 *
 * Every value resolves from a token (CP5 — no hex, no raw px). The token mapping
 * (prototype CSS → app token) is the one in the task's Design Spec § Token
 * mapping. Flagged substitutions: the prototype's pale brand/red tint fills have
 * no token slot — we render a `--color-bg-muted` fill + carry the brand/danger
 * signal in text + a hairline accent/danger border.
 */

import type { CSSProperties } from 'react';

const HAIRLINE = '0.0625rem'; // hairline border, expressed in rem (CP5).

// ---------------------------------------------------------------------------
// Group chrome — the flat bordered `.setgroup` block (replaces Card-in-Card).
// ---------------------------------------------------------------------------

export const setgroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--color-bg-primary)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-4)',
};

export const h4Style: CSSProperties = {
  margin: `0 0 var(--space-1)`,
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
};

export const hintStyle: CSSProperties = {
  margin: `0 0 var(--space-3)`,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 'var(--line-height-normal)',
};

// `.whatsbtn` — the quiet "What's this?" toggle in a group summary. A real
// underlined text button that reveals the group's explanation in place; the
// labels carry first-time navigation, so the prose only appears when asked
// (the surface reads light). Hover → accent via responsive.css (inline can't
// express :hover). Pushed to the right of the title with marginLeft:auto.
export const whatsBtnStyle: CSSProperties = {
  marginLeft: 'auto',
  border: 0,
  background: 'none',
  color: 'var(--color-text-muted)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  fontFamily: 'var(--font-family-sans)',
  lineHeight: 1,
  padding: `var(--space-1) var(--space-1)`,
  textDecoration: 'underline',
  textUnderlineOffset: '0.125rem',
};

// `.subnote` — a sentence-case caption that hangs under a `.subhead`, keeping
// the role label short ("Substitutes") and moving the descriptive clause out of
// the cramped uppercase line.
export const subnoteStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 'var(--line-height-normal)',
  margin: `calc(-1 * var(--space-1)) 0 var(--space-2)`,
};

// Uppercase, letter-spaced group sub-label.
export const subheadStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
  margin: `var(--space-3) 0 var(--space-2)`,
};

// `.setlabel` — Retailers / Stores sub-row labels in Where to buy.
export const setlabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
  margin: `var(--space-3) 0 var(--space-2)`,
};

// ---------------------------------------------------------------------------
// `.pinrow` — a bordered product/preference row (name meta + rolebtn + ✕).
// ---------------------------------------------------------------------------

export const pinrowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3)',
  marginBottom: 'var(--space-2)',
};

export const pinrowMetaStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const pinrowNmStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

export const pinrowCadStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

export const atstoreStyle: CSSProperties = {
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-bold)',
};

// `.rolebtn` — pill-outline flip button (Make substitute / Make preferred).
export const rolebtnStyle: CSSProperties = {
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  borderRadius: 'var(--radius-pill)',
  padding: `var(--space-2) var(--space-3)`,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// The bare ✕ remove glyph (not a Button).
export const pinrowXStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  padding: `var(--space-1) var(--space-1)`,
  lineHeight: 1,
};

// ---------------------------------------------------------------------------
// `.tags` chip rows — `.tag` (pref) / `.tag.sub` / `.tag.excl` / `.tag.add`.
// ---------------------------------------------------------------------------

export const tagsRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
  alignItems: 'center',
};

const tagBaseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  padding: `var(--space-2) var(--space-3)`,
  borderRadius: 'var(--radius-pill)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
};

// Preferred chip — accent text + accent border on a muted fill (flag 1), ★.
export const tagPrefStyle: CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-accent-primary)',
  borderColor: 'var(--color-accent-primary)',
};

// Substitute chip — muted fill, secondary text.
export const tagSubStyle: CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-secondary)',
  borderColor: 'var(--color-bg-muted)',
};

// Exclude chip — danger text + danger border on a muted fill (flag 1), 🚫.
export const tagExclStyle: CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-status-danger)',
  borderColor: 'var(--color-status-danger)',
};

// "+ Add" chip — dashed border, transparent fill, muted text; opens a search.
export const tagAddStyle: CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-muted)',
  borderStyle: 'dashed',
  borderColor: 'var(--color-border-strong)',
  cursor: 'pointer',
};

export const tagXStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'pointer',
  lineHeight: 1,
};

// ---------------------------------------------------------------------------
// `.setsearch` — single bordered row with a 🔍 glyph + borderless input.
// ---------------------------------------------------------------------------

export const setsearchStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3)',
  marginTop: 'var(--space-3)',
};

export const setsearchInputStyle: CSSProperties = {
  border: 0,
  outline: 0,
  flex: 1,
  minWidth: 0,
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  backgroundColor: 'transparent',
  color: 'var(--color-text-primary)',
};

// The "only at my stores" toggle, below the search row as a light hint.
export const toggleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-2)',
};

// ---------------------------------------------------------------------------
// `.presultbox` / `.presult` / `.miniadd` — search results.
// ---------------------------------------------------------------------------

export const presultboxStyle: CSSProperties = {
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  marginTop: 'var(--space-2)',
  overflow: 'hidden',
};

export const presultStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  // Let the action group (`presultRbStyle`) drop below the name+meta when the
  // row is too narrow (the sheet body at 360/390) instead of squeezing the name
  // into a per-word column.
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
  padding: 'var(--space-3)',
  borderBottomWidth: HAIRLINE,
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
};

export const presultMetaStyle: CSSProperties = {
  flex: '1 1 60%',
  // Keep the meta from collapsing so narrow that the name wraps per word; below
  // this it forces the action buttons onto the next line (wrap) instead.
  minWidth: '8rem',
};

export const presultNmStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

export const presultCadStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-1)',
};

export const presultRbStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  flex: '0 0 auto',
  flexWrap: 'wrap',
};

const miniaddBaseStyle: CSSProperties = {
  border: 0,
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  padding: `var(--space-2) var(--space-3)`,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// `.miniadd.pref` — solid accent fill, inverse text.
export const miniaddPrefStyle: CSSProperties = {
  ...miniaddBaseStyle,
  backgroundColor: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
};

// `.miniadd.sub` — muted fill, secondary text.
export const miniaddSubStyle: CSSProperties = {
  ...miniaddBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-secondary)',
};

// Don't-suggest action — like `.sub` but danger text (the firm-control signal).
export const miniaddExclStyle: CSSProperties = {
  ...miniaddBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-status-danger)',
};

// ---------------------------------------------------------------------------
// `.sizeunitrow` / `.unitseg` — the Size "Show in" display-unit switch (T36 /
// ADR-015). A label + a row of rounded display-unit PILLS (T36.3 — was a
// squared segmented strip; restyled to match the prototype `.unitseg button`,
// `border-radius:var(--r-pill)`). The current unit is the filled pill, the rest
// outlined. Count items have no forms → the row isn't shown. Lives inside the
// Size bottom-sheet (T36.3) — the row spaces label ⇄ pills like the proto.
// ---------------------------------------------------------------------------

export const sizeUnitRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
  marginTop: 'var(--space-2)',
  marginBottom: 'var(--space-3)',
};

export const sizeUnitLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
};

// The pill row wrapper — pills wrap; gap between them (no shared border, each
// pill is its own rounded chip).
export const unitSegStyle: CSSProperties = {
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
};

const unitSegBtnBaseStyle: CSSProperties = {
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  fontFamily: 'var(--font-family-sans)',
  borderRadius: 'var(--radius-pill)',
  padding: `var(--space-2) var(--space-3)`,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  minHeight: '2.125rem',
};

// Off pill — muted fill, secondary text, subtle border (outlined).
export const unitSegBtnStyle: CSSProperties = {
  ...unitSegBtnBaseStyle,
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-secondary)',
  borderColor: 'var(--color-border-strong)',
};

// On pill — accent fill, inverse text, accent border (the current unit).
export const unitSegBtnOnStyle: CSSProperties = {
  ...unitSegBtnBaseStyle,
  backgroundColor: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderColor: 'var(--color-accent-primary)',
};

// ---------------------------------------------------------------------------
// `.rolebtn` size opener (T36.3) — the compact Size control in Basics. Shows
// the current resolved measurement + a trailing chevron (e.g. `4 L ⌄`); tapping
// it opens the Size bottom-sheet. Matches the prototype's `.rolebtn` opener
// (`500 mL ⌄`): pill-outline, self-aligned left, current value as the label.
// ---------------------------------------------------------------------------

export const sizeOpenerStyle: CSSProperties = {
  ...rolebtnStyle,
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  fontFamily: 'var(--font-family-sans)',
};

// The trailing chevron glyph in the opener (heavier, muted).
export const sizeOpenerChevronStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 1,
};

// The Size sheet sub copy under the title (the proto's `#choose-sub`).
export const sizeSheetSubStyle: CSSProperties = {
  margin: `0 0 var(--space-3)`,
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
  lineHeight: 'var(--line-height-normal)',
};

// ---------------------------------------------------------------------------
// `.subhead` + `.presult.pick` / `.pickmark` — the Size "Pick your pack" list
// (T36.3). The prototype renders the packs not as chips but as full-width rows
// (one per pack) under a "Pick your pack" subhead; the current pack carries a
// leading ✓ pickmark, regardless of provenance (system default OR user pick).
// ---------------------------------------------------------------------------

// "Pick your pack" subhead — matches `.subhead` but with no top margin (it sits
// directly under the "Show in" switch, like the prototype's `margin-top:0`).
export const pickListSubheadStyle: CSSProperties = {
  ...subheadStyle,
  marginTop: 'var(--space-2)',
};

// `.presult.pick` — a full-width tappable pack row (leading pickmark + label).
export const pickRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: `var(--space-3)`,
  borderBottomWidth: HAIRLINE,
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

// `.presult.pick.on` — the current pack row carries a muted fill.
export const pickRowOnStyle: CSSProperties = {
  ...pickRowStyle,
  backgroundColor: 'var(--color-bg-muted)',
};

// `.pickmark` — the fixed-width leading ✓ slot (accent), empty on non-current
// rows so the labels stay aligned in a column.
export const pickMarkStyle: CSSProperties = {
  width: '1.125rem',
  flex: '0 0 auto',
  textAlign: 'center',
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-bold)',
};

// The pack list wrapper — a bordered box the rows sit inside (the last row's
// bottom border is hidden so the box edge isn't doubled).
export const pickListStyle: CSSProperties = {
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  marginTop: 'var(--space-2)',
  overflow: 'hidden',
};

// ---------------------------------------------------------------------------
// `.stepper` — How often (−/value/+).
// ---------------------------------------------------------------------------

export const cadrowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
};

export const cadWordStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

export const stepperStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
};

export const stepperBtnStyle: CSSProperties = {
  border: 0,
  backgroundColor: 'var(--color-bg-muted)',
  width: '2.25rem',
  height: '2.125rem',
  fontSize: 'var(--font-size-md)',
  cursor: 'pointer',
  color: 'var(--color-text-primary)',
  display: 'grid',
  placeItems: 'center',
};

export const stepperValueStyle: CSSProperties = {
  width: '2.75rem',
  textAlign: 'center',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
};

// "Let us learn it" clear link (cadenceDays → null) — rolebtn-styled text link.
export const learnLinkStyle: CSSProperties = {
  background: 'none',
  border: 0,
  color: 'var(--color-accent-primary)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  cursor: 'pointer',
  padding: 0,
};

// ---------------------------------------------------------------------------
// `details.setgroup` — collapsed extras.
// ---------------------------------------------------------------------------

export const detailsSummaryStyle: CSSProperties = {
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  listStyle: 'none',
  display: 'flex',
  gap: 'var(--space-2)',
  alignItems: 'center',
};

// `.sumttl` — the foldable section's title in a `<summary>` (Brand / Store
// preferences). Takes the leading space so the value line sits to its right.
export const sumttlStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

// `.sumval` — the muted summary value line shown beside the title while the
// foldable is collapsed (e.g. "★ Kikkoman · 2 subs"). Pushed to the right.
export const sumvalStyle: CSSProperties = {
  marginLeft: 'auto',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-regular)',
  color: 'var(--color-text-muted)',
  textAlign: 'right',
};

// ---------------------------------------------------------------------------
// Basics group — the always-open compact top group (How many · Size · How
// often). Each sub-control is a `basicsRow` separated by a hairline rule.
// ---------------------------------------------------------------------------

export const basicsRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  paddingTop: 'var(--space-3)',
  marginTop: 'var(--space-3)',
  borderTopWidth: HAIRLINE,
  borderTopStyle: 'solid',
  borderTopColor: 'var(--color-border-subtle)',
};

export const basicsLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

export const fieldrowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  marginTop: 'var(--space-3)',
};

export const fieldrowLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
};

// ---------------------------------------------------------------------------
// Shared misc.
// ---------------------------------------------------------------------------

export const mutedStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
};

export const errorStyle: CSSProperties = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
  marginBottom: 'var(--space-2)',
};

// ---------------------------------------------------------------------------
// `.addpref` — the quiet text-link action that opens the bounded search sheet
// (T27.2.7.10). A link (not a dashed drop-zone) because the action is "find a
// product"; the pins lists below already carry the visual weight.
// ---------------------------------------------------------------------------

export const addprefStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minHeight: '2.5rem',
  margin: `var(--space-1) 0 var(--space-2)`,
  padding: `var(--space-2) 0`,
  background: 'none',
  border: 0,
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  cursor: 'pointer',
};

// The "+" glyph in the add-link, heavier than the label.
export const addprefPlusStyle: CSSProperties = {
  fontSize: 'var(--font-size-md)',
  fontWeight: 'var(--font-weight-bold)',
  lineHeight: 1,
};

// ---------------------------------------------------------------------------
// `.automatch` — the Auto-matched block (T27.2.7.10). A collapsed, secondary
// group listing the system-linked products with Prefer / Avoid. Separated from
// the manual pins by a hairline top rule.
// ---------------------------------------------------------------------------

export const automatchStyle: CSSProperties = {
  marginTop: 'var(--space-3)',
  borderTopWidth: HAIRLINE,
  borderTopStyle: 'solid',
  borderTopColor: 'var(--color-border-subtle)',
  paddingTop: 'var(--space-3)',
};

export const automatchSummaryStyle: CSSProperties = {
  cursor: 'pointer',
  listStyle: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
  padding: `var(--space-1) 0`,
};

// The "11" count pill beside the Auto-matched label.
export const amcountStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-muted)',
  color: 'var(--color-text-secondary)',
  fontWeight: 'var(--font-weight-bold)',
  fontSize: 'var(--font-size-xs)',
  borderRadius: 'var(--radius-pill)',
  padding: `0 var(--space-2)`,
  lineHeight: 'var(--line-height-normal)',
};

// The "auto" tag chip on an auto-matched row's name.
export const amtagStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-pill)',
  padding: `0 var(--space-1)`,
  marginLeft: 'var(--space-2)',
  verticalAlign: 'middle',
};

// `.rolebtn.avoid` — the Avoid flip on an auto-matched row (muted, vs Prefer).
export const rolebtnAvoidStyle: CSSProperties = {
  ...rolebtnStyle,
  color: 'var(--color-text-muted)',
};

// "connected" badge on an already-pinned search result.
export const connectedBadgeStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
  borderWidth: HAIRLINE,
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  padding: `var(--space-1) var(--space-2)`,
};
